// frontend/src/components/UserChain.js
import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { getApiUrl } from '../config';
import './UserChain.css';
import InvitePathModal from './InvitePathModal';
// SendMessageModal больше не импортируется здесь, так как управляется из App.js
// import SendMessageModal from './SendMessageModal';

// --- Вспомогательные функции ---
const logToConsole = (message) => {
  const truncated = message && message.length > 500 ? message.substring(0, 500) + "..." : message;
  console.log(`[UserChain] ${truncated}`);
};

function debounce(func, wait) {
  let timeout;
  return function(...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

// --- Компонент карточки пользователя (БЕЗ кнопки Написать) ---
const UserCard = React.memo(({ user, telegramId, onClick, cardRef, matchingTokens, activeSearchQuery }) => {
    const renderCount = useRef(0);
    renderCount.current += 1;

    // console.log(`[UserCard ${user.telegram_id}] Рендер. Пропсы:`, { matchingTokens, activeSearchQuery }); // Отладочный лог

    const isCurrentUser = user.telegram_id === telegramId;

    let avatarSrc = '/avatars/default-avatar.png';
    if (user.avatar) {
        if (user.avatar.startsWith('/')) {
            avatarSrc = user.avatar;
        } else if (user.avatar.startsWith('avatars/')) {
            avatarSrc = '/' + user.avatar;
        }
    }

    const isAlliedChainUser = user.chain_info && !user.chain_info.is_same_chain;

    let tokensToShow = [];
    const searchTerm = activeSearchQuery?.trim().toLowerCase();
    const displayQuery = activeSearchQuery?.trim();

    if (displayQuery) {
      tokensToShow = [displayQuery];
    } else if (matchingTokens && matchingTokens.length > 0) {
      // Фильтруем FTS токены, чтобы убрать дубликаты и привести к нижнему регистру для сравнения (если нужно)
      tokensToShow = [...new Set(matchingTokens.map(token => token.toLowerCase()))];
      // Ограничим количество отображаемых токенов для компактности
      tokensToShow = tokensToShow.slice(0, 5); // Показываем не более 5 токенов
    }


    return (
        <div
            className={`user-card ${isCurrentUser ? 'is-self' : ''} ${isAlliedChainUser ? 'allied-chain' : ''}`} // Добавил класс allied-chain
            onClick={() => onClick(user)}
            ref={cardRef}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onClick(user)}
        >
            {/* Аватар */}
            <div className="user-avatar">
                <img
                    src={avatarSrc}
                    alt={`Аватар ${user.name || user.username}`}
                    onError={(e) => {
                        console.warn(`[UserCard ${user.telegram_id}] Ошибка аватара: ${e.target.src}`);
                        e.target.onerror = null;
                        e.target.src = '/avatars/default-avatar.png';
                    }}
                />
            </div>

            {/* Информация */}
            <div className="user-info">
                <div className="user-name">
                    {user.name || user.username || `User ${user.telegram_id}`}
                    {isCurrentUser && (
                        <span className="status-badge status-info">Вы</span>
                    )}
                    {isAlliedChainUser && (
                        <span className="status-badge chain-badge">Союз</span> // Используем новый класс
                    )}
                </div>

                {user.city && (
                    <div className="user-location">
                        <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2" fill="none">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        <span>{user.city}</span>
                    </div>
                )}

                {tokensToShow.length > 0 && (
                  <div className="matching-tokens">
                    <span className="tokens-label">
                      {searchTerm ? 'Найдено по запросу:' : 'Общие интересы:'}
                    </span>
                    <div className="tokens-container">
                      {tokensToShow.map((token, index) => (
                        <span
                          key={index}
                          className={`token-chip ${searchTerm ? 'highlighted-search-match' : ''}`}
                        >
                          {token}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            {/* --- КНОПКА ДЕЙСТВИЯ (НАПИСАТЬ) ПОЛНОСТЬЮ УДАЛЕНА ОТСЮДА --- */}

        </div>
    );
});
// -------------------------------------------------------------------------

// Основной компонент UserChain (forwardRef и остальное)
const UserChain = forwardRef(({
  telegramId,
  onViewUserProfile,
  filterCity,
  searchScope = 'all',
  searchQuery = '',
  onSectionChange
}, ref) => {

    // ... (Состояния useState) ...
    const [chainData, setChainData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [pageSize] = useState(20);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchType, setSearchType] = useState('all'); // 'all' (совпадения/все) или 'invited' (мои инвайты)
    const [invitePathData, setInvitePathData] = useState([]);
    const [showInvitePathModal, setShowInvitePathModal] = useState(false);
    const [isLoadingPath, setIsLoadingPath] = useState(false);
    const [errorPath, setErrorPath] = useState(null);
    // Состояния для SendMessageModal больше не нужны здесь
    const loadMoreRef = useRef(null);
    const currentUserCardRef = useRef(null);
    const chainContainerRef = useRef(null);
    const chainRenderCount = useRef(0);
    chainRenderCount.current += 1;
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery); // Инициализируем из props
    const [isTyping, setIsTyping] = useState(false);
    const isRequestInProgressRef = useRef(false);

    // Обработчик изменения типа поиска (тумблер)
    const handleSearchTypeChange = (event) => {
        const isChecked = event.target.checked;
        const newSearchType = isChecked ? 'invited' : 'all'; // Вкл: 'all', Выкл: 'invited'
        logToConsole(`Изменение типа поиска (тумблер) на: ${newSearchType}`);
        setSearchType(newSearchType);
        // Сбрасываем localSearchQuery при переключении на "Мои инвайты", если там был текст
        if (newSearchType === 'invited' && localSearchQuery) {
            setLocalSearchQuery('');
            // Отправляем событие с пустым запросом, чтобы App.js тоже обновился
            window.dispatchEvent(new CustomEvent('search-query-changed', { detail: '' }));
        }
        // useEffect [searchType] позаботится о вызове fetchChain и сбросе пагинации
    };

    // --- Функции useCallback ---

    // Скролл к текущему пользователю
    const scrollToCurrentUser = useCallback(() => {
        if (currentUserCardRef.current) {
          currentUserCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          logToConsole('Прокрутка к текущему пользователю (вызвано извне)');
        } else {
          logToConsole('Текущий пользователь не найден на видимой части цепочки.');
        }
    }, []);

    // Скролл к верху цепочки
    const scrollToTop = useCallback(() => {
        if (chainContainerRef.current) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          // chainContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' }); // Если скроллится только контейнер
          logToConsole('Прокрутка к верху цепочки (вызвано извне)');
        }
    }, []);

    // Предоставляем методы наружу через ref
    useImperativeHandle(ref, () => ({
        scrollToCurrentUser,
        scrollToTop,
        refreshChain: () => fetchChain() // Обновляем метод для новой сигнатуры
    }));

    // Функция для получения пути приглашений
    const fetchInvitePath = useCallback(async (clickedUserId) => {
      if (!telegramId || !clickedUserId || telegramId === clickedUserId) {
        logToConsole('Отмена запроса пути: недостаточно ID или клик на себя');
        return;
      }
      setIsLoadingPath(true);
      setErrorPath(null);
      try {
        logToConsole(`Запрос пути приглашений: ${telegramId} -> ${clickedUserId}`);
        const formData = new FormData();
        formData.append('current_user_id', telegramId);
        formData.append('target_user_id', clickedUserId);
        const response = await fetch(getApiUrl('/api/get_invite_path'), { method: 'POST', body: formData });
        if (!response.ok) {
          const errorText = await response.text();
          logToConsole(`Ошибка получения пути: ${response.status} - ${errorText}`);
          throw new Error(`Ошибка запроса пути: ${response.status}`);
        }
        const data = await response.json();
        logToConsole(`Путь получен: ${data.length} пользователей`);
        if (data && Array.isArray(data) && data.length > 0) {
          setInvitePathData(data);
          setShowInvitePathModal(true);
        } else {
          logToConsole('Путь пуст или некорректный формат ответа');
          setErrorPath('Не удалось получить путь между пользователями');
        }
      } catch (err) {
        logToConsole(`Ошибка при получении пути: ${err.message}`);
        setErrorPath(err.message);
      } finally {
        setIsLoadingPath(false);
      }
    }, [telegramId]);

    // Обработчик клика по карточке
    const handleUserCardClick = useCallback((user) => {
         console.log(`Клик по карточке пользователя: ${user.telegram_id}`);
         if (user.telegram_id !== telegramId) {
             console.log('Показываем профиль другого пользователя');
             if (onViewUserProfile) {
                 onViewUserProfile(user);
             }
         } else {
             // Можно добавить переход на свой профиль, если нужно
             // onSectionChange('profile');
             console.log('Это текущий пользователь, переходим на свой профиль');
             if (onSectionChange) onSectionChange('profile');
         }
     }, [telegramId, onViewUserProfile, onSectionChange]); // Добавили onSectionChange

    // Отправка события изменения поиска (debounce)
    const sendSearchEvent = useCallback(
         debounce((query) => {
             setIsTyping(false);
             // Важно: сравниваем с searchQuery из props, а не localSearchQuery
             if (query !== searchQuery) {
                 logToConsole(`Отправка события search-query-changed: ${query}`);
                 window.dispatchEvent(new CustomEvent('search-query-changed', { detail: query }));
             } else {
                 logToConsole(`Поисковый запрос не изменился ('${query}'), событие не отправлено`);
             }
         }, 800), // 800ms задержка
     [searchQuery] // Зависим от searchQuery из props
     );

    // Обработчик ввода в поле поиска
    const handleSearchInputChange = (e) => {
         const value = e.target.value;
         setLocalSearchQuery(value); // Обновляем локальное состояние немедленно
         setIsTyping(true); // Показываем индикатор ввода
         sendSearchEvent(value); // Вызываем debounced функцию для отправки события
     };

    // Обработчик клика по пользователю из модального окна пути
    const handleUserClickFromModal = useCallback((user) => {
        logToConsole(`Клик по пользователю ${user.telegram_id} из модального окна пути.`);
        setShowInvitePathModal(false); // Закрываем модальное окно пути
        handleUserCardClick(user); // Вызываем основной обработчик клика по карточке
    }, [handleUserCardClick]);

    // --- Завершение функции fetchChain ---
    const fetchChain = useCallback(() => {
        const currentTelegramId = telegramId;
        // Используем localSearchQuery для немедленного отклика на ввод,
        // но для API запроса лучше использовать searchQuery из props,
        // который обновится после debounce и вызовет useEffect
        const currentSearchQuery = searchQuery; // Берем из props
        const currentFilterCity = filterCity;
        const currentSearchScope = searchScope;
        const currentSearchType = searchType; // Тип поиска: 'all', 'invited', 'similar'

        logToConsole(`>>> fetchChain Вызван. Тип: ${currentSearchType}, Запрос: "${currentSearchQuery}", Город: "${currentFilterCity}", Область: ${currentSearchScope}`);

        if (isRequestInProgressRef.current) {
          logToConsole("<<< fetchChain Прерван: Запрос уже выполняется.");
          return;
        }
        if (!currentTelegramId) {
          setError("Не указан ID пользователя");
          setLoading(false);
          logToConsole("<<< fetchChain Прерван: Отсутствует telegramId.");
          return;
        }

        setLoading(true);
        isRequestInProgressRef.current = true;
        setError(null);
        // Сбрасываем данные перед новым запросом, чтобы не показывать старые результаты
        setChainData([]);
        setTotalUsers(0);
        setCurrentPage(1); // Всегда начинаем с первой страницы при новом поиске
        setHasMore(false);

        let apiUrl = '';
        const requestData = new FormData();
        requestData.append('telegram_id', currentTelegramId);

        // Определяем URL и параметры в зависимости от типа поиска
        if (currentSearchType === 'invited') {
            logToConsole("Запрос типа 'invited'");
            apiUrl = getApiUrl('/api/search_users');
            requestData.append('invited_only', 'true');
        } else if (currentSearchQuery || currentFilterCity) {
            logToConsole("Запрос типа 'search_users' (по запросу/городу)");
            apiUrl = getApiUrl('/api/search_users');
            if (currentSearchQuery) requestData.append('query', currentSearchQuery);
            if (currentFilterCity) requestData.append('city', currentFilterCity);
            // Область поиска 'scope' не используется в search_users, но можем оставить для будущих доработок
            // if (currentSearchScope) requestData.append('scope', currentSearchScope);
        } else {
            logToConsole("Запрос типа 'find_similar_users' (по умолчанию)");
            apiUrl = getApiUrl('/api/find_similar_users');
            // Дополнительные параметры для find_similar_users не требуются
        }

        logToConsole(`API URL: ${apiUrl}`);
        // Логируем тело запроса (если это не GET)
        if (requestData.entries()) {
            let bodyLog = '';
            for (let pair of requestData.entries()) { bodyLog += `${pair[0]}=${pair[1]}; `; }
            logToConsole(`Request Body: ${bodyLog}`);
        }


        // --- Запрос к API ---
        fetch(apiUrl, {
            method: 'POST', // Все эндпоинты теперь POST
            body: requestData
        })
        .then(response => {
            logToConsole(`API Response Status: ${response.status}`);
            if (!response.ok) {
                // Пытаемся прочитать тело ошибки
                return response.text().then(text => {
                   logToConsole(`API Error Response Body: ${text.substring(0, 200)}`);
                   let errorMsg = `Ошибка ${response.status}`;
                   try {
                       const errData = JSON.parse(text);
                       errorMsg = errData.error || errorMsg;
                   } catch(e) { /* ignore json parse error */ }
                   throw new Error(errorMsg);
                });
            }
            return response.json();
        })
        .then(data => {
            logToConsole(`API Response Data: ${JSON.stringify(data).substring(0, 200)}...`);

            if (data.error) { // Проверяем поле error в ответе
                 throw new Error(data.error);
            }

            const users = data.users || []; // Получаем массив пользователей

            // Фильтруем текущего пользователя, ЕСЛИ это не поиск приглашенных
            const filteredUsers = currentSearchType !== 'invited'
              ? users.filter(user => user.telegram_id !== currentTelegramId)
              : users;

            logToConsole(`Получено пользователей: ${users.length}, После фильтрации текущего: ${filteredUsers.length}`);

            setChainData(filteredUsers);
            setHasMore(data.has_next !== undefined ? data.has_next : false); // Пагинация пока отключена для поиска/похожих
            setTotalUsers(filteredUsers.length); // Используем длину отфильтрованного массива
            setCurrentPage(data.current_page !== undefined ? data.current_page : 1);
            setTotalPages(data.total_pages !== undefined ? data.total_pages : 1);

            logToConsole(`<<< fetchChain Завершен успешно. Пользователей: ${filteredUsers.length}`);
        })
        .catch(err => {
            logToConsole(`<<< fetchChain Ошибка: ${err.message}`);
            setError(err.message);
            setChainData([]); // Очищаем данные при ошибке
            setTotalUsers(0);
            setHasMore(false);
        })
        .finally(() => {
            setLoading(false);
            isRequestInProgressRef.current = false;
            logToConsole("<<< fetchChain finally: Загрузка завершена.");
        });

    }, [telegramId, searchScope, filterCity, searchQuery, searchType]); // Добавили searchType

    // Обработчик закрытия модального окна пути
    const handleClosePathModal = useCallback(() => {
        setShowInvitePathModal(false);
    }, []);


    // --- Хуки useEffect ---

    // Синхронизация внешнего searchQuery с локальным (если изменился извне)
    useEffect(() => {
      if (searchQuery !== localSearchQuery) {
        logToConsole(`Синхронизация searchQuery prop -> localSearchQuery: '${searchQuery}'`);
        setLocalSearchQuery(searchQuery);
        // Сбрасываем флаг isTyping, так как изменение пришло извне
        setIsTyping(false);
      }
    }, [searchQuery]); // Только при изменении searchQuery из props

    // Эффект для запуска fetchChain при изменении КРИТЕРИЕВ ПОИСКА
    useEffect(() => {
        if (!telegramId) {
            logToConsole("useEffect [criteria]: Пропуск, нет telegramId");
            return;
        };
        // Запускаем новый поиск ТОЛЬКО если изменились props, отвечающие за критерии
        logToConsole(`useEffect [criteria]: Запуск fetchChain из-за изменения критериев.`);
        fetchChain();

    // Мы зависим от telegramId и КРИТЕРИЕВ поиска/фильтрации
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [telegramId, filterCity, searchQuery, searchScope, searchType]); // fetchChain зависит от этих параметров

    // Эффект для ПАГИНАЦИИ (сейчас не используется для поиска/похожих/приглашенных)
    useEffect(() => {
      if (loadingMore || !loadMoreRef.current || !hasMore || searchType !== 'all' /* Пагинация только для 'all'? */) return;

      const currentRef = loadMoreRef.current;
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            // Пока пагинация реализована только в старом get_chain, здесь ничего не делаем
            logToConsole("Достигнут триггер 'Загрузить еще', но пагинация для этого типа поиска не реализована.");
            // Если бы пагинация была, вызвали бы:
            // fetchChain(currentPage + 1, true);
          }
        },
        { threshold: 0.5 }
      );

      observer.observe(currentRef);
      return () => {
        if (currentRef) observer.unobserve(currentRef);
      };
    }, [hasMore, loadingMore, currentPage, searchType /* Добавили searchType */]);


    // --- Рендер контента (список пользователей или сообщения) ---
    const renderContent = () => {
        if (loading) {
            return (
                <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>Ищем пользователей...</p>
                    <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
                        {searchType === 'invited'
                            ? 'Загрузка списка приглашенных вами пользователей'
                            : (!filterCity && !searchQuery)
                                ? 'Поиск похожих пользователей на основе вашего профиля'
                                : 'Поиск по указанным критериям'
                        }
                    </p>
                </div>
            );
        }
        if (error) {
            return (
                <div className="error-container" style={{ padding: '20px', textAlign: 'center', border: '1px solid red', background:'#ffebee' }}>
                    <h2>Ошибка загрузки</h2>
                    <p>{error}</p>
                    <button onClick={() => fetchChain()} className="button button-primary">
                        Попробовать снова
                    </button>
                </div>
            );
        }
        if (chainData.length === 0) {
            return (
                <div className="empty-filters-container" style={{textAlign:'center', padding:'30px', background:'#f9f9f9', borderRadius:'5px', margin:'20px 0'}}>
                    {searchType === 'invited' ? (
                        <>
                            <h3>Вы никого не пригласили</h3>
                            <p>Когда вы пригласите друзей, они появятся здесь.</p>
                            <div style={{fontSize:'36px', margin:'20px 0'}}>✉️</div>
                        </>
                    ) : (!filterCity && !searchQuery) ? (
                        <>
                            <h3>Не найдены похожие пользователи</h3>
                            <p>Заполните подробнее свой профиль или используйте поле поиска выше для поиска по ключевым словам.</p>
                            <div style={{fontSize:'36px', margin:'20px 0'}}>🔍</div>
                        </>
                    ) : (
                        <>
                            <h3>Пользователи не найдены</h3>
                            <p>По вашему запросу ({searchQuery || filterCity}) ничего не найдено.</p>
                            <p>Попробуйте изменить критерии поиска.</p>
                            <div style={{fontSize:'36px', margin:'20px 0'}}>🤷</div>
                        </>
                    )}
                </div>
            );
        }

        // --- Список найденных пользователей ---
        return (
            <>
                <div className="results-info">
                    <p>
                        {searchType === 'invited' ? `Приглашено:` : `Найдено:`} {totalUsers}
                        {/* | Показано: {chainData.length} (Пагинации пока нет) */}
                    </p>
                    {/* {totalPages > 1 && <p>Страница {currentPage} из {totalPages}</p>} */}
                </div>
                <div className="chain-wrapper">
                    {chainData.map((user) => (
                        <UserCard
                            key={user.telegram_id}
                            user={user}
                            telegramId={telegramId}
                            onClick={handleUserCardClick}
                            cardRef={user.telegram_id === telegramId ? currentUserCardRef : null}
                            matchingTokens={user.matching_fts_tokens} // Передаем FTS токены
                            activeSearchQuery={searchQuery} // Передаем активный поисковый запрос
                        />
                    ))}
                </div>
                {/* Кнопка Загрузить еще (если пагинация будет) */}
                {/* {hasMore && !loadingMore && (...)} */}
                {/* {loadingMore && <div className="loading-indicator">Загрузка еще...</div>} */}
            </>
        );
    };

    // --- Основной рендер компонента ---
    return (
        <div className="user-chain-container" ref={chainContainerRef}>

            {/* --- Поле поиска с фильтром (фиксированное) --- */}
            <div className="search-box">
                <input
                    id="searchInput"
                    type="text"
                    placeholder={searchType === 'invited' ? "Поиск среди приглашенных..." : "Поиск по интересам, навыкам..."}
                    value={localSearchQuery}
                    onChange={handleSearchInputChange}
                    className="search-input"
                    style={{marginBottom: '10px'}}
                    autoComplete="off"
                    disabled={searchType === 'invited'} // Блокируем поиск если смотрим приглашенных
                />
                {isTyping && (
                    <div style={{fontSize: '12px', color: '#666', marginTop: '5px', fontStyle: 'italic', position: 'absolute', bottom: '-18px', left: '15px'}}>
                        Ввод...
                    </div>
                )}
                
                {/* Перемещенный блок фильтра */}
                <div className="filter-controls-inline">
                    <span className="filter-label">Мои инвайты</span>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={searchType === 'invited'} // Включен (справа), если ищем 'all'
                            onChange={handleSearchTypeChange}
                            disabled={loading} // Блокируем во время загрузки
                        />
                        <span className="slider round"></span>
                    </label>
                </div>
            </div>

            {/* Блок контента (список или сообщения) */}
            <div className="chain-content">
                {/* Отображение состояния загрузки, ошибки или списка пользователей */}
                {loading ? (
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                        <p>Ищем пользователей...</p>
                        <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
                            {searchType === 'invited'
                                ? 'Загрузка списка приглашенных вами пользователей'
                                : (!filterCity && !searchQuery)
                                    ? 'Поиск похожих пользователей на основе вашего профиля'
                                    : 'Поиск по указанным критериям'
                            }
                        </p>
                    </div>
                ) : error ? (
                    <div className="error-container" style={{ padding: '20px', textAlign: 'center', border: '1px solid red', background:'#ffebee' }}>
                        <h2>Ошибка загрузки</h2>
                        <p>{error}</p>
                        <button onClick={() => fetchChain()} className="button button-primary">
                            Попробовать снова
                        </button>
                    </div>
                ) : (
                    <>
                        {/* --- Отображение результатов --- */}
                        {chainData.length === 0 ? (
                            // --- Сообщение "Ничего не найдено" ---
                            <div className="empty-filters-container" style={{textAlign:'center', padding:'30px', background:'#f9f9f9', borderRadius:'5px', margin:'20px 0'}}>
                                {searchType === 'invited' ? (
                                    <>
                                        <h3>Вы никого не пригласили</h3>
                                        <p>Когда вы пригласите друзей, они появятся здесь.</p>
                                        <div style={{fontSize:'36px', margin:'20px 0'}}>✉️</div>
                                    </>
                                ) : (!filterCity && !searchQuery) ? (
                                    <>
                                        <h3>Не найдены похожие пользователи</h3>
                                        <p>Заполните подробнее свой профиль или используйте поле поиска выше для поиска по ключевым словам.</p>
                                        <div style={{fontSize:'36px', margin:'20px 0'}}>🔍</div>
                                    </>
                                ) : (
                                    <>
                                        <h3>Пользователи не найдены</h3>
                                        <p>По вашему запросу ({searchQuery || filterCity}) ничего не найдено.</p>
                                        <p>Попробуйте изменить критерии поиска.</p>
                                        <div style={{fontSize:'36px', margin:'20px 0'}}>🤷</div>
                                    </>
                                )}
                            </div>
                        ) : (
                           // --- Список найденных пользователей ---
                           <>
                                <div className="results-info">
                                     <p>
                                         {searchType === 'invited' ? `Приглашено:` : `Найдено:`} {totalUsers}
                                         {/* | Показано: {chainData.length} (Пагинации пока нет) */}
                                     </p>
                                     {/* {totalPages > 1 && <p>Страница {currentPage} из {totalPages}</p>} */}
                                </div>
                                <div className="chain-wrapper">
                                    {chainData.map((user) => (
                                        <UserCard
                                            key={user.telegram_id}
                                            user={user}
                                            telegramId={telegramId}
                                            onClick={handleUserCardClick}
                                            cardRef={user.telegram_id === telegramId ? currentUserCardRef : null}
                                            // onSendMessage УДАЛЕНО
                                            matchingTokens={user.matching_fts_tokens} // Передаем FTS токены
                                            activeSearchQuery={searchQuery} // Передаем активный поисковый запрос
                                        />
                                    ))}
                                </div>
                           </>
                        )}
                        {/* --- Конец отображения результатов --- */}
                    </>
                )}
            </div>

            {/* Модальное окно пути приглашений */}
            <InvitePathModal
                pathData={invitePathData}
                showModal={showInvitePathModal}
                onClose={handleClosePathModal}
                isLoadingPath={isLoadingPath} // Передаем состояние загрузки
                errorPath={errorPath}       // Передаем ошибку
                currentUserTelegramId={telegramId} // Передаем ID текущего пользователя
                onUserClick={handleUserClickFromModal} // Передаем обработчик клика из модалки
            />

            {/* Индикатор загрузки/ошибки пути можно оставить, если нужен */}
            {/* {isLoadingPath && <div className="path-loading-overlay">...</div>} */}
            {/* {errorPath && <div className="path-error-message">...</div>} */}

        </div>
    );
});

export default UserChain;